import { useEffect, useLayoutEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { whenPasswordChangeRequired, whenSignedOut } from '../api';
import { clearCustomerSession, getCustomerSession } from '../lib/customerSession';
import { clearDriverSession, isDriverSignedIn } from '../lib/driverSession';

/** What a sign-in page says when the session ended under the person (router state `sessionEnded`). */
export const SESSION_ENDED = 'Your session has ended. Please sign in again.';

/**
 * The app's one answer to the API's two session events (`src/api/index.js`).
 * Mounted once, inside the router; draws nothing.
 *
 * - **401 UNAUTHENTICATED**, the token is dead (expired, revoked, the account
 *   erased): whoever is signed in is signed out here too and lands on their
 *   sign-in, which says why. Before this nothing listened: the profile stayed,
 *   the gates let the person through, and every screen showed the server's
 *   "Authentication is required…" (emulator, 3 Oct 2026).
 * - **403 PASSWORD_CHANGE_REQUIRED**, a password carried over from the old
 *   app (D15): the server walls every other route, so the person is taken to
 *   choose their own, whichever screen asked.
 *
 * Only while a session exists: a second 401 of the same moment, or a call
 * made after signing out, finds none and does nothing. A wrong password at
 * sign-in is INVALID_CREDENTIALS, which the client reports as neither event.
 *
 * The move is made at once (`flushSync`). The screen that asked is unmounted
 * before its own refusal reaches it, so its own "401, back to sign-in"
 * (several screens have one) finds itself cancelled and does not replace the
 * router state that carries the sentence. That holds while the router updates
 * outside a transition (React Router 6 without `v7_startTransition`): a move
 * to v7 is held by `SessionListener.test.js`.
 */
export default function SessionListener() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const here = useRef({ navigate, pathname });

  useLayoutEffect(() => {
    here.current = { navigate, pathname };
  });

  useEffect(() => {
    const go = (to, state) => {
      if (here.current.pathname === to) return;
      flushSync(() => here.current.navigate(to, { replace: true, state }));
    };

    const offSignedOut = whenSignedOut(() => {
      const rider = isDriverSignedIn();
      if (!rider && !getCustomerSession()) return;
      // One token for both realms: a profile kept beside it is dead too.
      clearDriverSession();
      clearCustomerSession();
      go(rider ? '/driver/login' : '/login', { sessionEnded: true });
    });

    const offPasswordChange = whenPasswordChangeRequired(() => {
      if (isDriverSignedIn()) go('/driver/choose-password');
      else if (getCustomerSession()) go('/choose-password');
    });

    return () => {
      offSignedOut();
      offPasswordChange();
    };
  }, []);

  return null;
}
